// React Query hooks for org-level user management — list, create, update, disable, enable.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgUsersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { CreateOrganisationUserInput, UpdateOrganisationUserInput } from '../types';

export function useOrgUsers() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';

  return useQuery({
    queryKey: ['orgUsers', orgId],
    queryFn: () => orgUsersApi.list(orgId),
    enabled: !!orgId,
  });
}

export function useCreateOrgUser() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOrganisationUserInput) => orgUsersApi.create(orgId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgUsers', orgId] });
    },
  });
}

export function useUpdateOrgUser() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: UpdateOrganisationUserInput }) =>
      orgUsersApi.update(orgId, userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgUsers', orgId] });
    },
  });
}

export function useDisableOrgUser() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => orgUsersApi.disable(orgId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgUsers', orgId] });
    },
  });
}

export function useEnableOrgUser() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => orgUsersApi.enable(orgId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgUsers', orgId] });
    },
  });
}

export function useResendOrgUserInvitation() {
  const { user } = useAuth();
  const orgId = user?.organisationId || '';

  return useMutation({
    mutationFn: (userId: string) =>
      orgUsersApi.resendInvitation(orgId, userId),
  });
}
